import { useState } from 'react';
import { toast } from 'react-toastify';
import { datadogLogs } from '@datadog/browser-logs';
import { GroupBy, useStartDonationExportMutation } from 'services/donationExportApi';
import { runDonationExportFlow, type SelectedDateRange } from 'services/exports/donationExportFlow';
import { describeExportFailure, ExportUserError } from 'services/exports/helpers';
import { useStartEventTicketExportMutation } from 'services/eventTicketDownloadApi';
import { useAppSelector } from 'helpers/hooks';
import { getUserClientId } from 'selectors/user';
import { DashboardScreen } from 'helpers/mixpanelEvents';
import ReportOptions from './reportOptions';
import { EventTicketReportOptions } from './eventTicketReportOptions';
import { hasInvalidClientId } from './utils';

interface Props {
  screen: DashboardScreen;
}

export const ReportsDashboard = ({ screen }: Props) => {
  const [startDonationExport] = useStartDonationExportMutation();
  const [startEventTicketExport] = useStartEventTicketExportMutation();
  const [isReportRunning, setIsReportRunning] = useState(false);
  const clientId = useAppSelector(getUserClientId);

  const runExportWithToast = async (startExportFn: () => Promise<string>, filename?: string) => {
    setIsReportRunning(true);
    const toastId = toast.loading('Processing your CSV export. This may take a moment.', {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
    });

    try {
      await runDonationExportFlow({
        startExport: startExportFn,
        filename,
      });

      toast.update(toastId, {
        render: 'Download is ready in your “Downloads” folder.',
        type: 'success',
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });
    } catch (error: unknown) {
      const { message, severity, logMessage, logContext } = describeExportFailure(error);

      // Backend guardrails are load conditions, not defects — the backend logs them at INFO too.
      // Keep the severity honest so an admin error monitor doesn't fire on a working guardrail.
      if (severity === 'info') {
        datadogLogs.logger.info(logMessage, logContext);
      } else {
        datadogLogs.logger.error(
          logMessage,
          logContext,
          error instanceof Error ? error : undefined
        );
      }

      // Always 'error', never 'warning', even when severity is 'info': severity exists to keep working
      // guardrails off the error monitor, but from the partner's side no file arrived either way.
      toast.update(toastId, {
        render: message,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        closeOnClick: true,
      });
    } finally {
      setIsReportRunning(false);
    }
  };

  const runReport = async (
    clientId: number,
    selectedRange: SelectedDateRange,
    recipientsSelected: string[],
    groupBy?: GroupBy
  ) => {
    const startExport = async () => {
      if (hasInvalidClientId(clientId)) {
        throw new ExportUserError('Invalid client ID. Please refresh and try again.');
      }

      return startDonationExport({
        clientId,
        start_date: selectedRange.start.toISOString().split('T')[0],
        end_date: selectedRange.end.toISOString().split('T')[0],
        recipients_to_include: recipientsSelected,
        group_by: groupBy ?? null,
      }).unwrap();
    };

    await runExportWithToast(startExport);
  };

  const runEventTicketReport = async (eventSlug: string) => {
    const startExport = async () => {
      if (typeof clientId !== 'number' || hasInvalidClientId(clientId)) {
        throw new ExportUserError('Invalid client ID. Please refresh and try again.');
      }
      return startEventTicketExport({
        client_id: clientId,
        event_page_slug: eventSlug,
      }).unwrap();
    };

    await runExportWithToast(startExport, 'oath_event_tickets_export.csv');
  };

  return (
    <div>
      <ReportOptions
        clientId={clientId ?? 0}
        title="Summary Report"
        itemName="donors"
        groupBy="transaction"
        runReport={runReport}
        exportButtonEnabled={!isReportRunning}
        screen={screen}
      />
      <ReportOptions
        clientId={clientId ?? 0}
        title="Detailed Recipient Report"
        itemName="recipients"
        includeRecipientsSelector={true}
        runReport={runReport}
        exportButtonEnabled={!isReportRunning}
        screen={screen}
      />
      <EventTicketReportOptions
        clientId={clientId ?? 0}
        exportButtonEnabled={!isReportRunning}
        runReport={runEventTicketReport}
        screen={screen}
      />
    </div>
  );
};

export default ReportsDashboard;
